import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_DATA_FACT, SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import './styles.scss'
import Checkbox from '../../../Form/Checkbox'
import Icon from '../../../Icon'
import Button from '../../../Form/Button'
import { withTranslation } from 'react-i18next'
import { getComponentLabel, getTrackingFactLabel } from '../../helper'
import FactInstance from './fact-instance'
import produce from 'immer'
import Modal from '../../../Modal'
import get from 'lodash.get'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'

class FactCheckbox extends PureComponent {
  state = {
    showConfirmModal: false
  }

  onCheckboxChange = (e) => {
    if (e.target.checked) {
      activityTrackingService.trackEvent(trackingEvent.checkFact, {
        name: getTrackingFactLabel(this.props.blueprint, this.props.factsheetBlueprint)
      })
      this.props.addFactInstance(this.props.blueprint.id, this.getNewFact())
    } else if (this.isRemovalConfirmationRequired()) {
      this.setState({ showConfirmModal: true })
    } else {
      activityTrackingService.trackEvent(trackingEvent.uncheckFact, {
        name: getTrackingFactLabel(this.props.blueprint, this.props.factsheetBlueprint)
      })
      this.props.deleteFact(this.props.blueprint.id)
    }
  }

  onConfirmModalCancel = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ showConfirmModal: false })
  }

  onConfirmModalConfirm = () => {
    this.setState({ showConfirmModal: false })
    this.props.deleteFact(this.props.blueprint.id)
  }

  isChecked () {
    return Array.isArray(this.props.data) && this.props.data.length > 0
  }

  isRemovalConfirmationRequired () {
    if (this.props.data.length > 1) {
      return true
    }
    return !!(this.props.data[0] && (this.props.data[0].hasOwnProperty('attributes') && Object.keys(this.props.data[0].attributes).length > 0))

  }

  getNewFact () {
    return {
      'factDefId': this.props.blueprint.id
    }
  }

  onAddNewFactsInstanceClick (multiplicity) {
    if (this.props.data.length + 1 > multiplicity[1]) {
      return
    }
    this.props.addFactInstance(this.props.blueprint.id, this.getNewFact())
  }

  onFactInstanceDeleteClick = (index) => {
    this.props.deleteFactInstance(this.props.blueprint.id, index)
  }

  onSelectedVariantsChange = (selectedVariantIds, factInstanceIndex) => {
    this.props.putFactInstance(
      this.props.blueprint.id, factInstanceIndex,
      produce(this.props.data[factInstanceIndex], draft => {
        draft.appliesTo = selectedVariantIds
      })
    )
  }

  createFactInstances (fact) {
    const multiplicity = get(fact, 'multiplicity', 1)
    let instanceCount = 0
    const maxAllowedInstanceCount = multiplicity[1]
    if (this.props.data) {
      instanceCount = (maxAllowedInstanceCount < this.props.data.length) ? maxAllowedInstanceCount : this.props.data.length
    }
    const instances = Array.from(Array(instanceCount).keys()).map((key) => {
      let validationStatus = undefined
      if (typeof this.props.validationStatus !== 'undefined') {
        validationStatus = this.props.validationStatus.factInstances[key]
      }
      const factInstanceExpanded = this.props.expandedFactInstances && this.props.expandedFactInstances[key] === true
      return (
        <FactInstance key={key} withHeader={instanceCount > 1}
                      blueprint={this.props.blueprint}
                      data={this.props.data[key]}
                      onCollapseChange={this.onFactInstanceCollapseChange}
                      factsheetBlueprint={this.props.factsheetBlueprint}
                      onFactInstanceDeleteClick={this.onFactInstanceDeleteClick}
                      putFactInstanceExpandedStatus={this.props.putFactInstanceExpandedStatus}
                      onSelectedVariantsChange={this.onSelectedVariantsChange}
                      validationStatus={validationStatus}
                      collapsed={!factInstanceExpanded} index={key}/>
      )
    })
    const factLabel = getComponentLabel(
      this.props.blueprint,
      fact)
    return (
      <div className={'fact-checkbox-instances'}>
        <React.Fragment>
          {instances}
          {
            (instanceCount > 0 && instanceCount + 1 <= maxAllowedInstanceCount) && (
              <div className={'fact-checkbox-add-instance'}>
                <Button classNames={'Button-Light Button-small'}
                        onClick={() => {this.onAddNewFactsInstanceClick(multiplicity)}}>
                  <div className={'Button-Content'}>
                    <Icon type={'plus'} size={'extra-small'}/>
                    <span>{this.props.t('ADD_ANOTHER', { 'label': factLabel })}</span>
                  </div>
                </Button>
              </div>
            )
          }
        </React.Fragment>
      </div>
    )
  }

  render () {
    const checked = this.isChecked()
    const factDefId = this.props.blueprint.id
    const fact = this.props.factsheetBlueprint.factTree.facts[factDefId]
    const showActions = !!fact.definition
    const label = getComponentLabel(
      this.props.blueprint,
      fact)

    return (
      <div className={'fact-checkbox'}>
        <div className={'fact-checkbox-header'}>
          <Checkbox onChange={this.onCheckboxChange} value={checked}
                    label={label}/>
          {
            showActions && (
              <div className={'fact-checkbox-actions'}>
                {fact.definition && <Icon type={'how'} tooltip={fact.definition}/>}
              </div>
            )
          }
        </div>
        {this.createFactInstances(fact)}
        <Modal show={this.state.showConfirmModal}
               onEscapeKeyDown={this.onConfirmModalCancel}
               onBackdropClick={this.onConfirmModalCancel}>
          <div className={'modal-confirm-removal'}>
            <p className={'modal-message'}>Do you really want to remove this fact with all instances and attributes?</p>
            <Button classNames={'Button-Light'} align={'right'}
                    onClick={this.onConfirmModalCancel}>{this.props.t('BUTTON_CANCEL')}</Button>
            <Button align={'right'} onClick={this.onConfirmModalConfirm}>{this.props.t('BUTTON_CONFIRM')}</Button>
          </div>
        </Modal>
      </div>
    )
  }
}

FactCheckbox.propTypes = {
  blueprint: PropTypes.shape({ components: PropTypes.array.isRequired }).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired,
  data: PropTypes.arrayOf(PropTypes.shape(SHAPE_DATA_FACT)),
  validationStatus: PropTypes.object,
  deleteFact: PropTypes.func.isRequired,
  addFactInstance: PropTypes.func.isRequired,
  putFactInstance: PropTypes.func.isRequired,
  putFactInstanceExpandedStatus: PropTypes.func.isRequired,
  deleteFactInstance: PropTypes.func.isRequired,
  expandedFactInstances: PropTypes.array,
}
export default withTranslation()(FactCheckbox)
